import { Empty, Tooltip } from "antd";
import { memo, useMemo, useState } from "react";
import type { SourceDashboard, SourceType } from "../types";

const sourceMeta: Record<SourceType, { label: string; color: string }> = {
  PLAYER_REFERRAL: { label: "玩家推荐", color: "#1677ff" },
  BILIBILI: { label: "B站外招", color: "#36cfc9" },
  DOUYIN: { label: "抖音外招", color: "#722ed1" },
  LEGACY: { label: "历史来源", color: "#faad14" },
  UNSPECIFIED: { label: "未选择来源", color: "#8c8c8c" }
};

const DONUT_CENTER = 100;
const DONUT_OUTER_RADIUS = 84;
const DONUT_INNER_RADIUS = 55;

function pointOnDonut(angle: number, radius: number) {
  return {
    x: DONUT_CENTER + Math.cos(angle) * radius,
    y: DONUT_CENTER + Math.sin(angle) * radius
  };
}

function donutSectorPath(startAngle: number, endAngle: number) {
  const span = endAngle - startAngle;
  if (span >= Math.PI * 2 - 0.0001) {
    return [
      `M ${DONUT_CENTER} ${DONUT_CENTER - DONUT_OUTER_RADIUS}`,
      `A ${DONUT_OUTER_RADIUS} ${DONUT_OUTER_RADIUS} 0 1 1 ${DONUT_CENTER - 0.001} ${DONUT_CENTER - DONUT_OUTER_RADIUS}`,
      `L ${DONUT_CENTER - 0.001} ${DONUT_CENTER - DONUT_INNER_RADIUS}`,
      `A ${DONUT_INNER_RADIUS} ${DONUT_INNER_RADIUS} 0 1 0 ${DONUT_CENTER} ${DONUT_CENTER - DONUT_INNER_RADIUS}`,
      "Z"
    ].join(" ");
  }

  const outerStart = pointOnDonut(startAngle, DONUT_OUTER_RADIUS);
  const outerEnd = pointOnDonut(endAngle, DONUT_OUTER_RADIUS);
  const innerStart = pointOnDonut(startAngle, DONUT_INNER_RADIUS);
  const innerEnd = pointOnDonut(endAngle, DONUT_INNER_RADIUS);
  const largeArc = span > Math.PI ? 1 : 0;
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${DONUT_OUTER_RADIUS} ${DONUT_OUTER_RADIUS} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${DONUT_INNER_RADIUS} ${DONUT_INNER_RADIUS} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    "Z"
  ].join(" ");
}

export const SourceDonut = memo(function SourceDonut({
  rows
}: {
  rows: SourceDashboard["sourceCounts"];
}) {
  const [activeSource, setActiveSource] = useState<SourceType | null>(null);
  // 历史迁移数据不属于本轮来源漏斗，不展示在当前来源构成中。
  const displayRows = useMemo(() => rows.filter((row) => row.source !== "LEGACY"), [rows]);
  const total = displayRows.reduce((sum, row) => sum + row.count, 0);
  const segments = useMemo(() => {
    let cursor = -Math.PI / 2;
    return displayRows.map((row) => {
      const sweep = total ? row.count / total * Math.PI * 2 : 0;
      const startAngle = cursor;
      const endAngle = cursor + sweep;
      cursor = endAngle;
      const middleAngle = startAngle + sweep / 2;
      return {
        ...row,
        path: donutSectorPath(startAngle, endAngle),
        offsetX: Math.cos(middleAngle) * 8,
        offsetY: Math.sin(middleAngle) * 8
      };
    });
  }, [displayRows, total]);
  const activeSegment = activeSource ? segments.find((segment) => segment.source === activeSource) ?? null : null;

  if (!total) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无真实来源数据" />;
  }

  return (
    <div className="source-donut-wrap">
      <div className="source-donut" onMouseLeave={() => setActiveSource(null)}>
        <svg viewBox="0 0 200 200" role="img" aria-label="来源构成">
          {segments.map((segment) => {
            const selected = segment.source === activeSource;
            return (
              <path
                aria-label={`${sourceMeta[segment.source].label}：${segment.count} 人`}
                className={`source-donut__slice${selected ? " source-donut__slice--active" : ""}`}
                d={segment.path}
                fill={sourceMeta[segment.source].color}
                key={segment.source}
                role="button"
                tabIndex={0}
                transform={selected ? `translate(${segment.offsetX} ${segment.offsetY})` : undefined}
                onBlur={() => setActiveSource(null)}
                onFocus={() => setActiveSource(segment.source)}
                onMouseEnter={() => setActiveSource(segment.source)}
              />
            );
          })}
        </svg>
        <div className="source-donut__core">
          <strong>{(activeSegment?.count ?? total).toLocaleString()}</strong>
          <span>{activeSegment ? `${sourceMeta[activeSegment.source].label} · ${activeSegment.count} 人` : "总申请"}</span>
        </div>
      </div>
      <div className="source-legend">
        {displayRows.map((row) => (
          <div className="source-legend__row" key={row.source}>
            <i style={{ background: sourceMeta[row.source].color }} />
            <span>{sourceMeta[row.source].label}</span>
            <b>{row.count}</b>
            <em>{total ? `${(row.count / total * 100).toFixed(1)}%` : "0%"}</em>
          </div>
        ))}
      </div>
    </div>
  );
});

export const Funnel = memo(function Funnel({
  rows
}: {
  rows: SourceDashboard["funnel"];
}) {
  if (!rows.length || rows.every((row) => row.value === 0)) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无真实转化数据" />;
  }
  const max = Math.max(1, rows[0]?.value ?? 1);
  return (
    <div className="funnel">
      {rows.map((row, index) => {
        const width = Math.max(34, row.value / max * 100);
        const previous = index ? rows[index - 1]?.value ?? row.value : row.value;
        const retention = previous ? row.value / previous * 100 : 0;
        return (
          <div className="funnel__stage" key={row.key}>
            <div className="funnel__meta">
              <span>{String(index + 1).padStart(2, "0")} · {row.label}</span>
              <b>{row.value.toLocaleString()}</b>
            </div>
            <div className="funnel__track">
              <div style={{ width: `${width}%` }} />
            </div>
            <small>{index === 0 ? "入口基准" : `环节留存 ${retention.toFixed(1)}%`}</small>
          </div>
        );
      })}
    </div>
  );
});

export const TrendChart = memo(function TrendChart({
  rows,
  emptyDescription = "当前周期暂无真实变化"
}: {
  rows: SourceDashboard["trend"];
  emptyDescription?: string;
}) {
  if (!rows.length || rows.every((row) => row.total === 0 && row.qualified === 0)) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyDescription} />;
  }
  const width = 720;
  const height = 220;
  const padding = 24;
  const plotHeight = height - padding * 2;
  const max = Math.max(1, ...rows.flatMap((row) => [row.total, row.qualified]));
  const point = (index: number, key: "total" | "qualified") => {
    const row = rows[index];
    const x = padding + (rows.length <= 1 ? 0 : index / (rows.length - 1) * (width - padding * 2));
    const y = height - padding - (row?.[key] ?? 0) / max * plotHeight;
    return { x, y };
  };
  const points = (key: "total" | "qualified") => rows.map((_, index) => {
    const coordinate = point(index, key);
    return `${coordinate.x},${coordinate.y}`;
  }).join(" ");
  return (
    <div className="trend">
      <div className="trend__legend">
        <span><i className="trend__dot trend__dot--total" />新增申请</span>
        <span><i className="trend__dot trend__dot--qualified" />当日生效邀请</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="近期来源趋势">
        <defs>
          <linearGradient id="trend-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#1677ff" stopOpacity=".2" />
            <stop offset="1" stopColor="#1677ff" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((ratio) => (
          <line
            key={ratio}
            x1={padding}
            y1={padding + plotHeight * ratio}
            x2={width - padding}
            y2={padding + plotHeight * ratio}
            stroke="#f0f0f0"
          />
        ))}
        {rows.length > 1 && (
          <polygon
            points={`${padding},${height - padding} ${points("total")} ${width - padding},${height - padding}`}
            fill="url(#trend-fill)"
          />
        )}
        <polyline points={points("total")} fill="none" stroke="#1677ff" strokeWidth="4" />
        <polyline points={points("qualified")} fill="none" stroke="#36cfc9" strokeWidth="3" />
        {rows.map((row, index) => {
          const totalPoint = point(index, "total");
          const qualifiedPoint = point(index, "qualified");
          return (
            <Tooltip
              key={row.date}
              mouseEnterDelay={0}
              placement="top"
              title={(
                <div>
                  <div>{row.date}</div>
                  <div>新增申请：{row.total} 人</div>
                  <div>当日生效：{row.qualified} 人</div>
                </div>
              )}
            >
              <g
                aria-label={`${row.date}：新增申请 ${row.total} 人，当日生效邀请 ${row.qualified} 人`}
                className="trend__node"
                role="button"
                tabIndex={0}
              >
                <circle cx={totalPoint.x} cy={totalPoint.y} r="6" fill="#fff" stroke="#1677ff" strokeWidth="3" />
                <circle cx={qualifiedPoint.x} cy={qualifiedPoint.y} r="5" fill="#fff" stroke="#36cfc9" strokeWidth="3" />
                <circle cx={totalPoint.x} cy={totalPoint.y} r="14" fill="transparent" />
                <circle cx={qualifiedPoint.x} cy={qualifiedPoint.y} r="14" fill="transparent" />
              </g>
            </Tooltip>
          );
        })}
      </svg>
      <div className="trend__axis">
        {rows.map((row) => <span key={row.date}>{row.date.slice(5)}</span>)}
      </div>
    </div>
  );
});

export { sourceMeta };
